import { useState, useEffect, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ContactPage() {
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Contact Us – Ganga Bhakti | Music Production Company New Delhi India';

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'page-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Ganga Bhakti – Music Production Company New Delhi',
      url: `${import.meta.env.VITE_SITE_URL || ''}/contact`,
      description: 'Contact Ganga Bhakti for music production, recording, video production, artist management and digital promotion services. Call Rohit Sharma: 09990194423 or Puneet Sharma: 9911122200.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || ''}/` },
          { '@type': 'ListItem', position: 2, name: 'Contact', item: `${import.meta.env.VITE_SITE_URL || ''}/contact` },
        ],
      },
      mainEntity: {
        '@type': 'LocalBusiness',
        '@id': `${import.meta.env.VITE_SITE_URL || ''}/#business`,
        name: 'Ganga Bhakti',
        alternateName: 'Ganga Bhakti',
        image: 'https://static.readdy.ai/image/417385ceaaad418fef1f23ebf0e5a264/c6746d9834be35a6b3054e6592c21a0a.png',
        url: import.meta.env.VITE_SITE_URL || '',
        telephone: ['+919990194423', '+919911122200'],
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'B-40 DSIDC Complex, Kirti Nagar',
          addressLocality: 'New Delhi',
          postalCode: '110015',
          addressCountry: 'IN',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 28.6517,
          longitude: 77.1508,
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            opens: '10:00',
            closes: '19:00',
          },
        ],
        sameAs: [
          'https://www.facebook.com/gangamusicofficial',
          'https://www.instagram.com/gangadigitalofficial/',
          'https://www.youtube.com/@GangaCassette',
        ],
        priceRange: '₹₹₹',
      },
    });
    const existing = document.getElementById('page-jsonld');
    if (existing) existing.remove();
    document.head.appendChild(script);

    const t = setTimeout(() => setVisible(true), 100);
    return () => {
      clearTimeout(t);
      const el = document.getElementById('page-jsonld');
      if (el) el.remove();
    };
  }, []);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const name = (formData.get('name') as string) || '';
    const email = (formData.get('email') as string) || '';
    const phone = (formData.get('phone') as string) || '';
    const service = (formData.get('service') as string) || '';
    const subject = (formData.get('subject') as string) || '';
    const message = (formData.get('message') as string) || '';

    const text = `New Website Enquiry 🚀

Name: ${name}
Phone: ${phone}
Email: ${email}
Service: ${service || 'Not specified'}
Subject: ${subject}
Message: ${message}`;

    const encoded = encodeURIComponent(text);
    const waUrl = `https://wa.me/919990194423?text=${encoded}`;

    setSubmitted(true);
    form.reset();

    // Small delay so user sees the success message before redirect
    setTimeout(() => {
      window.open(waUrl, '_blank');
    }, 800);
  };

  return (
    <div className="bg-[#0d0d0d] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            alt="Ganga Bhakti contact us"
            className="w-full h-full object-cover object-top"
            src="https://readdy.ai/api/search-image?query=Indian%20music%20company%20office%20reception%2C%20professional%20modern%20interior%2C%20warm%20golden%20lighting%2C%20music%20industry%20corporate%20environment%2C%20elegant%20dark%20decor&width=1920&height=600&seq=contact_hero&orientation=landscape"
          />
          <div className="absolute inset-0 bg-black/85"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[#d4af37]"></span>
              <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">Get In Touch</span>
              <span className="w-8 h-px bg-[#d4af37]"></span>
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white mb-4">
              Contact <span className="text-[#d4af37]">Us</span>
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Ready to create something extraordinary? Reach out to our team and let's start your musical journey together.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - Info */}
            <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <h2 className="font-heading text-3xl font-bold text-white mb-8">
                Let's Create <span className="text-[#d4af37]">Together</span>
              </h2>

              {/* Contact Cards */}
              <div className="space-y-4 mb-8">
                <div className="glass-card rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 flex-shrink-0">
                    <i className="ri-map-pin-line text-[#d4af37] text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-semibold mb-3">Our Offices</div>
                    <div className="space-y-3">
                      <div>
                        <div className="text-[#d4af37] text-xs font-semibold uppercase tracking-wider mb-0.5">Head Office</div>
                        <div className="text-white/60 text-sm leading-relaxed">B-40 DSIDC Complex, Kirti Nagar<br />New Delhi - 110015, India</div>
                      </div>
                      <div className="w-full h-px bg-white/5"></div>
                      <div>
                        <div className="text-[#d4af37] text-xs font-semibold uppercase tracking-wider mb-0.5">Studio Office</div>
                        <div className="text-white/60 text-sm leading-relaxed">B-11 Phase 1, Mayapuri<br />New Delhi - 110064, India</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="glass-card rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 flex-shrink-0">
                      <i className="ri-phone-line text-[#d4af37] text-xl"></i>
                    </div>
                    <div className="text-white font-semibold">Contact Numbers</div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white/80 text-sm font-medium">Rohit Sharma</div>
                        <a href="tel:09990194423" className="text-[#d4af37] text-sm hover:text-white transition-colors">09990194423</a>
                      </div>
                      <div className="flex gap-2">
                        <a href="tel:09990194423" className="w-9 h-9 flex items-center justify-center rounded-full bg-[#d4af37]/10 border border-[#d4af37]/20 text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-all cursor-pointer">
                          <i className="ri-phone-line text-sm"></i>
                        </a>
                        <a href="https://wa.me/919990194423" target="_blank" rel="nofollow noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-green-500/10 border border-green-500/20 text-green-400 hover:bg-green-500 hover:text-white transition-all cursor-pointer">
                          <i className="ri-whatsapp-line text-sm"></i>
                        </a>
                      </div>
                    </div>
                    <div className="w-full h-px bg-white/5"></div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white/80 text-sm font-medium">Puneet Sharma</div>
                        <a href="tel:9911122200" className="text-[#d4af37] text-sm hover:text-white transition-colors">9911122200</a>
                      </div>
                      <div className="flex gap-2">
                        <a href="tel:9911122200" className="w-9 h-9 flex items-center justify-center rounded-full bg-[#d4af37]/10 border border-[#d4af37]/20 text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-all cursor-pointer">
                          <i className="ri-phone-line text-sm"></i>
                        </a>
                        <a href="https://wa.me/919911122200" target="_blank" rel="nofollow noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-green-500/10 border border-green-500/20 text-green-400 hover:bg-green-500 hover:text-white transition-all cursor-pointer">
                          <i className="ri-whatsapp-line text-sm"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="glass-card rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 flex-shrink-0">
                    <i className="ri-share-line text-[#d4af37] text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-semibold mb-3">Follow Us</div>
                    <div className="flex items-center gap-3">
                      <a href="https://www.facebook.com/gangamusicofficial" target="_blank" rel="nofollow noreferrer"
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877f2]/10 border border-[#1877f2]/20 text-[#1877f2] text-sm hover:bg-[#1877f2] hover:text-white transition-all cursor-pointer">
                        <i className="ri-facebook-fill"></i> Facebook
                      </a>
                      <a href="https://www.instagram.com/gangadigitalofficial/" target="_blank" rel="nofollow noreferrer"
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-400 text-sm hover:bg-pink-500 hover:text-white transition-all cursor-pointer">
                        <i className="ri-instagram-line"></i> Instagram
                      </a>
                      <a href="https://www.youtube.com/@GangaCassette" target="_blank" rel="nofollow noreferrer"
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm hover:bg-red-500 hover:text-white transition-all cursor-pointer">
                        <i className="ri-youtube-fill"></i> YouTube
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-4">
                <a href="tel:09990194423" className="flex items-center justify-center gap-2 py-4 rounded-2xl btn-gold text-black font-semibold text-sm whitespace-nowrap cursor-pointer transition-all hover:scale-105">
                  <i className="ri-phone-fill text-lg"></i> Call Now
                </a>
                <a href="https://wa.me/919990194423" target="_blank" rel="nofollow noreferrer" className="flex items-center justify-center gap-2 py-4 rounded-2xl bg-green-500 hover:bg-green-600 text-white font-semibold text-sm whitespace-nowrap cursor-pointer transition-all hover:scale-105">
                  <i className="ri-whatsapp-line text-lg"></i> WhatsApp
                </a>
              </div>
            </div>

            {/* Right - Form */}
            <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <div className="glass-card rounded-3xl p-8">
                <h3 className="font-heading text-2xl font-bold text-white mb-2">Send Us a Message</h3>
                <p className="text-white/50 text-sm mb-6 flex items-center gap-2">
                  <i className="ri-whatsapp-line text-green-400"></i>
                  Your message will be sent directly via WhatsApp
                </p>

                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-500/20 border border-green-500/30 mx-auto mb-4">
                      <i className="ri-whatsapp-line text-green-400 text-3xl"></i>
                    </div>
                    <h4 className="font-heading text-xl font-bold text-white mb-2">Opening WhatsApp!</h4>
                    <p className="text-white/60 text-sm">Your enquiry is being sent to our team via WhatsApp. We'll respond within a few hours.</p>
                    <button onClick={() => setSubmitted(false)} className="mt-6 px-6 py-2 rounded-full text-sm text-[#d4af37] border border-[#d4af37]/30 hover:bg-[#d4af37]/10 transition-all cursor-pointer">
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form
                    id="ganga-contact-form"
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="Your full name"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="your@email.com"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          placeholder="+91 XXXXX XXXXX"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Service Interested In</label>
                        <select
                          name="service"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                        >
                          <option value="YouTube Services" className="bg-[#111]">YouTube Services</option>
                          <option value="" className="bg-[#111]">Select a service</option>
                          <option value="Music Production" className="bg-[#111]">Music Production</option>
                          <option value="Audio Recording" className="bg-[#111]">Audio Recording</option>
                          <option value="Dubbing & Voice Over" className="bg-[#111]">Dubbing &amp; Voice Over</option>
                          <option value="Mixing & Mastering" className="bg-[#111]">Mixing &amp; Mastering</option>
                          <option value="Video Production" className="bg-[#111]">Video Production</option>
                          <option value="Artist Management" className="bg-[#111]">Artist Management</option>
                          <option value="Digital Promotion" className="bg-[#111]">Digital Promotion</option>
                          <option value="Other" className="bg-[#111]">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-white/70 text-sm mb-2">Subject *</label>
                      <input
                        type="text"
                        name="subject"
                        required
                        placeholder="How can we help you?"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-white/70 text-sm mb-2">Message *</label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="Tell us about your project, requirements, or any questions you have..."
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#d4af37]/50 transition-colors resize-none"
                      ></textarea>
                      <div className="text-white/30 text-xs mt-1 text-right">Max 500 characters</div>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-4 rounded-xl text-base font-semibold text-white whitespace-nowrap bg-green-500 hover:bg-green-600 cursor-pointer transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                    >
                      <i className="ri-whatsapp-line text-xl"></i>
                      Send via WhatsApp
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-3xl font-bold text-white mb-10 text-center">
            Find Our <span className="text-[#d4af37]">Offices</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Head Office — Kirti Nagar */}
            <div className="rounded-2xl overflow-hidden border border-[#d4af37]/15 bg-[#111]">
              <div className="px-5 py-4 flex items-start justify-between">
                <div>
                  <h3 className="text-white font-heading font-bold text-lg">Head Office — Kirti Nagar</h3>
                  <p className="text-white/50 text-sm mt-0.5">B-40 DSIDC Complex, Kirti Nagar, New Delhi - 110015</p>
                </div>
                <a
                  href="https://maps.google.com/?q=B-40+DSIDC+Complex+Kirti+Nagar+New+Delhi+110015"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/20 text-[#d4af37] text-xs font-medium hover:bg-[#d4af37]/20 transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ml-3"
                >
                  <i className="ri-external-link-line"></i> Open in Maps
                </a>
              </div>
              <div style={{ height: '280px' }}>
                <iframe
                  title="Ganga Bhakti Head Office - Kirti Nagar New Delhi"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.4!2d77.1508!3d28.6517!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d03c0a0a0a0a0%3A0x1a1a1a1a1a1a1a1a!2sKirti%20Nagar%2C%20New%20Delhi%2C%20Delhi%20110015!5e0!3m2!1sen!2sin!4v1700000000001!5m2!1sen!2sin"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            {/* Studio Office — Mayapuri */}
            <div className="rounded-2xl overflow-hidden border border-[#d4af37]/15 bg-[#111]">
              <div className="px-5 py-4 flex items-start justify-between">
                <div>
                  <h3 className="text-white font-heading font-bold text-lg">Studio Office — Mayapuri</h3>
                  <p className="text-white/50 text-sm mt-0.5">B-11 Phase 1, Mayapuri, New Delhi - 110064</p>
                </div>
                <a
                  href="https://maps.google.com/?q=B-11+Phase+1+Mayapuri+New+Delhi+110064"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/20 text-[#d4af37] text-xs font-medium hover:bg-[#d4af37]/20 transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ml-3"
                >
                  <i className="ri-external-link-line"></i> Open in Maps
                </a>
              </div>
              <div style={{ height: '280px' }}>
                <iframe
                  title="Ganga Bhakti Studio Office - Mayapuri New Delhi"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.9!2d77.1200!3d28.6350!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d03e0b0b0b0b0%3A0x2b2b2b2b2b2b2b2b!2sMayapuri%2C%20New%20Delhi%2C%20Delhi%20110064!5e0!3m2!1sen!2sin!4v1700000000002!5m2!1sen!2sin"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
